import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useApp } from '../../context/AppContext';
import { downloadPDF } from '../../utils/pdfUtils';
import { generateFilename } from '../../utils/fileUtils';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import './FormFiller.css';

// Set up the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const FormFiller = () => {
  const { currentFile, showNotification, setCurrentView } = useApp();
  const [pdfDoc, setPdfDoc] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [textBoxes, setTextBoxes] = useState([]);
  const [selectedTextBox, setSelectedTextBox] = useState(null);
  const [isAddingText, setIsAddingText] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dragState, setDragState] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!currentFile) return;

    const loadDocument = async () => {
      try {
        const arrayBuffer = await currentFile.file.arrayBuffer();
        const loadedPdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(loadedPdf);
        setPageCount(loadedPdf.numPages);
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadDocument();
  }, [currentFile]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(selectedPage + 1);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale: zoom });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, selectedPage, zoom]);

  const handleCanvasClick = (e) => {
    if (!isAddingText) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    const newTextBox = {
      id: Date.now(),
      pageIndex: selectedPage,
      position: { x, y },
      text: '',
      fontSize: 12,
      color: '#000000',
      isEditing: true
    };

    setTextBoxes(prev => [...prev, newTextBox]);
    setSelectedTextBox(newTextBox.id);
    setIsAddingText(false);
  };

  const handleTextChange = (id, text) => {
    setTextBoxes(prev => prev.map(tb =>
      tb.id === id ? { ...tb, text } : tb
    ));
  };

  const handleFontSizeChange = (id, fontSize) => {
    setTextBoxes(prev => prev.map(tb =>
      tb.id === id ? { ...tb, fontSize: parseInt(fontSize) } : tb
    ));
  };

  const handleColorChange = (id, color) => {
    setTextBoxes(prev => prev.map(tb =>
      tb.id === id ? { ...tb, color } : tb
    ));
  };

  const handleTextBoxMouseDown = (e, tb) => {
    e.stopPropagation();
    setSelectedTextBox(tb.id);

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    setDragState({
      textBoxId: tb.id,
      offsetX: mouseX - tb.position.x,
      offsetY: mouseY - tb.position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom - dragState.offsetX;
    const y = (e.clientY - rect.top) / zoom - dragState.offsetY;

    setTextBoxes(prev => prev.map(tb =>
      tb.id === dragState.textBoxId
        ? { ...tb, position: { x: Math.max(0, x), y: Math.max(0, y) } }
        : tb
    ));
  }, [dragState, zoom]);

  const handleMouseUp = useCallback(() => {
    setDragState(null);
  }, []);

  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);

  const handleRemoveTextBox = (id) => {
    setTextBoxes(prev => prev.filter(tb => tb.id !== id));
    if (selectedTextBox === id) {
      setSelectedTextBox(null);
    }
  };

  const handleSave = async () => {
    if (textBoxes.length === 0) {
      showNotification('Please add at least one text box', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      const pdfBytes = await currentFile.pdfDoc.save();
      const pdfDocLib = await PDFDocument.load(pdfBytes);
      const font = await pdfDocLib.embedFont(StandardFonts.Helvetica);

      for (const tb of textBoxes) {
        if (!tb.text.trim()) continue;

        const page = pdfDocLib.getPage(tb.pageIndex);
        const { height: pageHeight } = page.getSize();

        // Convert hex color to RGB
        const hexToRgb = (hex) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255
          } : { r: 0, g: 0, b: 0 };
        };

        const color = hexToRgb(tb.color);

        // Convert canvas Y to PDF Y
        const pdfY = pageHeight - tb.position.y - tb.fontSize;

        page.drawText(tb.text, {
          x: tb.position.x,
          y: pdfY,
          size: tb.fontSize,
          font,
          color: rgb(color.r, color.g, color.b)
        });
      }

      const filename = generateFilename(currentFile.name, 'filled');
      await downloadPDF(pdfDocLib, filename);
      showNotification('PDF with text downloaded successfully', 'success');
    } catch (error) {
      console.error('Error saving PDF:', error);
      showNotification('Failed to save PDF', 'error');
    }

    setIsSaving(false);
  };

  if (!currentFile) {
    return (
      <div className="form-filler-empty">
        <p>No PDF file loaded</p>
        <button className="btn btn-primary" onClick={() => setCurrentView('upload')}>
          Upload PDF
        </button>
      </div>
    );
  }

  return (
    <div className="form-filler-container">
      <div className="form-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('view')}>
          ← Back to Viewer
        </button>
        <h2>Add Text to PDF</h2>
        <div className="form-actions">
          <button
            className={`btn ${isAddingText ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setIsAddingText(!isAddingText)}
          >
            {isAddingText ? 'Cancel' : 'Add Text Box'}
          </button>
        </div>
      </div>

      <div className="form-content">
        <div className="form-sidebar">
          <h3>Pages</h3>
          <div className="page-list">
            {Array.from({ length: pageCount }, (_, i) => i).map((pageIndex) => (
              <button
                key={pageIndex}
                className={`page-item ${pageIndex === selectedPage ? 'active' : ''}`}
                onClick={() => setSelectedPage(pageIndex)}
              >
                <span>Page {pageIndex + 1}</span>
                {textBoxes.filter(tb => tb.pageIndex === pageIndex).length > 0 && (
                  <span className="text-badge">
                    {textBoxes.filter(tb => tb.pageIndex === pageIndex).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {selectedTextBox && (
            <div className="text-properties">
              <h3>Text Properties</h3>
              {(() => {
                const tb = textBoxes.find(t => t.id === selectedTextBox);
                if (!tb) return null;
                return (
                  <>
                    <div className="property-item">
                      <label>Font Size</label>
                      <input
                        type="number"
                        min="8"
                        max="72"
                        value={tb.fontSize}
                        onChange={(e) => handleFontSizeChange(tb.id, e.target.value)}
                      />
                    </div>
                    <div className="property-item">
                      <label>Color</label>
                      <input
                        type="color"
                        value={tb.color}
                        onChange={(e) => handleColorChange(tb.id, e.target.value)}
                      />
                    </div>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveTextBox(tb.id)}
                    >
                      Remove Text Box
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {textBoxes.length > 0 && (
            <div className="text-list">
              <h3>Text Boxes ({textBoxes.length})</h3>
              {textBoxes.map((tb) => (
                <div
                  key={tb.id}
                  className={`text-item ${selectedTextBox === tb.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPage(tb.pageIndex);
                    setSelectedTextBox(tb.id);
                  }}
                >
                  <span className="text-preview">{tb.text || '(empty)'}</span>
                  <span className="text-page">Page {tb.pageIndex + 1}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-canvas-area">
          <div className="canvas-controls">
            <div className="zoom-controls">
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.5))}>
                −
              </button>
              <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.min(prev + 0.25, 3))}>
                +
              </button>
            </div>
          </div>

          <div className="canvas-wrapper" onClick={() => setSelectedTextBox(null)}>
            {isAddingText && (
              <div className="placement-hint">
                Click on the PDF to add a text box
              </div>
            )}
            <div
              ref={containerRef}
              className="canvas-container"
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <canvas
                ref={canvasRef}
                className={`pdf-canvas ${isAddingText ? 'adding-text' : ''}`}
                onClick={handleCanvasClick}
              />
              {/* Render text boxes as overlays */}
              {textBoxes
                .filter(tb => tb.pageIndex === selectedPage)
                .map(tb => (
                  <div
                    key={tb.id}
                    className={`text-box-overlay ${selectedTextBox === tb.id ? 'selected' : ''}`}
                    style={{
                      position: 'absolute',
                      left: tb.position.x * zoom,
                      top: tb.position.y * zoom,
                      fontSize: tb.fontSize * zoom,
                      color: tb.color,
                      cursor: dragState?.textBoxId === tb.id ? 'grabbing' : 'grab'
                    }}
                    onMouseDown={(e) => handleTextBoxMouseDown(e, tb)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <textarea
                      value={tb.text}
                      onChange={(e) => handleTextChange(tb.id, e.target.value)}
                      placeholder="Type here..."
                      style={{
                        fontSize: tb.fontSize * zoom,
                        color: tb.color
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="form-footer">
        <button
          className="btn btn-success"
          onClick={handleSave}
          disabled={isSaving || textBoxes.length === 0}
        >
          {isSaving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            `Download PDF with Text (${textBoxes.length} text box${textBoxes.length !== 1 ? 'es' : ''})`
          )}
        </button>
      </div>
    </div>
  );
};

export default FormFiller;
